import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { SEOHead } from '../components/SEOHead';
import { StructuredData } from '../components/StructuredData';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { 
  Zap, 
  Phone, 
  ExternalLink, 
  Filter, 
  Search, 
  MapPin,
  TrendingUp,
  Shield,
  Leaf,
  Clock,
  Info,
  Award
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ElectricityComparatorPage = () => {
  const { t, i18n } = useTranslation();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(i18n.language === 'es' ? 'ES' : 'FR');

  const isSpanish = i18n.language === 'es';
  const specialist = getSpecialistMessage(selectedCountry, 'electricity');
  const saveaPhone = getSaveaPhone(selectedCountry, 'electricity', 'LP'); // Comparateur utilise LP
  const saveaPhoneTel = getSaveaPhoneTel(selectedCountry, 'electricity', 'LP');

  // Prix 2026 actualisés pour les fournisseurs (source: comparateurs janvier 2026)
  const providerPrices = {
    // France - Tarifs 2026 (après baisse TRVE)
    'EDF': { kwh: 0.1952, subscription: 15.47 },           // Tarif Bleu TRVE janvier 2026
    'Engie': { kwh: 0.1730, subscription: 14.97 },         // Offre Elec Référence
    'Ekwateur': { kwh: 0.1764, subscription: 19.81 },      // Zen Fixe Base
    'TotalEnergies France': { kwh: 0.1494, subscription: 17.74 }, // Offre fixe compétitive
    'Total Energies France': { kwh: 0.1494, subscription: 17.74 },
    'Octopus France': { kwh: 0.1627, subscription: 15.47 }, // Retiré - ne plus afficher
    'Mint Energie': { kwh: 0.1658, subscription: 15.47 },   // Offre verte
    'OHM': { kwh: 0.1686, subscription: 15.47 },            // Offre classique
    'Vattenfall': { kwh: 0.1720, subscription: 15.47 },     // Offre Électricité Eco
    'La Bellénergie': { kwh: 0.1825, subscription: 15.47 }, // Offre verte locale
    'Primeo Energie': { kwh: 0.1634, subscription: 15.47 }, // Offre compétitive
    'Alpiq': { kwh: 0.1793, subscription: 15.47 },          // Électricité Classique
    'Elmy': { kwh: 0.1906, subscription: 16.90 },           // Fixe 100% Verte
    'Alterna Energie': { kwh: 0.1825, subscription: 15.47 }, // Verte Française
    'Leclerc Energies': { kwh: 0.1780, subscription: 15.47 }, // Électricité Bleue
    'GRDF': { kwh: 0.1952, subscription: 15.47 },           // Référence réseau
    'Ilek': { kwh: 0.1951, subscription: 21.67 },           // ÉnRFixe+ Elec
    'Enedis': { kwh: 0.1952, subscription: 15.47 },         // Référence réseau
    'ENI': { kwh: 0.1857, subscription: 15.47 },            // Plenifix 1 an
    'Elecocité': { kwh: 0.1952, subscription: 15.47 },      // Aligné TRVE + projets citoyens
    // Espagne - Tarifs 2026 (mercado libre)
    'Curenergia': { kwh: 0.0742, subscription: 2.67 },      // PVPC valle
    'Endesa': { kwh: 0.0993, subscription: 2.67 },          // One Luz 3P llano
    'Energia XXi': { kwh: 0.1050, subscription: 2.67 },     // Tarifa estándar
    'Plenitude': { kwh: 0.1161, subscription: 2.27 },       // Fácil Luz fijo
    'Factor Energia': { kwh: 0.1100, subscription: 2.67 },  // Tarifa competitiva
    'Gana Energia': { kwh: 0.1080, subscription: 2.67 },    // Tarifa verde
    'Iberdrola': { kwh: 0.1150, subscription: 2.67 },       // Plan Estable
    'Holaluz': { kwh: 0.1200, subscription: 2.67 },         // 100% renovable
    'Naturgy': { kwh: 0.1192, subscription: 2.67 },         // Por Uso fijo
    'Repsol': { kwh: 0.1180, subscription: 2.67 },          // Tarifa Luz
    'Baser': { kwh: 0.1058, subscription: 2.67 },           // Tarifa Nufri valle
    'Octopus': { kwh: 0.0856, subscription: 2.67 },         // Octopus 3 valle
    'Total Energies': { kwh: 0.1150, subscription: 2.67 }   // Tarifa estándar
  };

  useEffect(() => {
    fetchProviders();
  }, [selectedCountry]);

  useEffect(() => {
    setSelectedCountry(i18n.language === 'es' ? 'ES' : 'FR');
  }, [i18n.language]);

  const fetchProviders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/providers?country=${selectedCountry}&vertical=NRJ`);
      
      // Enrich providers with pricing data et filtrer ENEDIS/GRDF
      const enrichedProviders = response.data
        .filter(provider => !['GRDF', 'Enedis'].includes(provider.name)) // Retirer gestionnaires réseau
        .map(provider => ({
          ...provider,
          pricing: providerPrices[provider.name] || { kwh: 0.1850, subscription: 10.00 },
          features: getProviderFeatures(provider),
          monthlyEstimate: calculateMonthlyEstimate(provider.name),
          // Mettre à jour logo Primeo
          logo_url: provider.name === 'Primeo Energie' ? '/images/logos/K0RTbiN.png' : provider.logo_url
        }));

      setProviders(enrichedProviders);
    } catch (error) {
      console.error('Error fetching providers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    } finally {
      setLoading(false);
    }
  };

  const getProviderFeatures = (provider) => {
    const features = [];
    
    if (provider.name.toLowerCase().includes('vert') || 
        provider.name.toLowerCase().includes('green') ||
        provider.name.toLowerCase().includes('eco') ||
        provider.name === 'Ilek' ||
        provider.name === 'Ekwateur') {
      features.push({ label: i18n.language === 'es' ? 'Energía verde' : 'Énergie verte', icon: Leaf, color: 'green' });
    }
    
    if (provider.name === 'EDF' || provider.name === 'Engie') {
      features.push({ label: i18n.language === 'es' ? 'Proveedor principal' : 'Fournisseur majeur', icon: Award, color: 'blue' });
    }
    
    features.push({ label: i18n.language === 'es' ? 'Atención al cliente' : 'Service client', icon: Shield, color: 'purple' });
    
    return features;
  };

  const calculateMonthlyEstimate = (providerName) => {
    const pricing = providerPrices[providerName];
    if (!pricing) return 120;
    
    // Estimation pour 300 kWh/mois (consommation moyenne foyer français)
    return Math.round((pricing.kwh * 300) + pricing.subscription);
  };

  const filteredProviders = providers.filter(provider =>
    provider.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const callProvider = (phone) => {
    window.open(`tel:${phone}`, '_self');
  };

  const visitWebsite = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const getCountryFlag = (country) => {
    return country === 'FR' ? '🇫🇷' : '🇪🇸';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <SEOHead type="electricity" />
      <StructuredData type="electricity-comparator" />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="electricity-comparator-title"
            >
              {t('tools.electricity.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {t('tools.electricity.description')}. 
              {i18n.language === 'es' 
                ? 'Encuentra el mejor proveedor de energía adaptado a tus necesidades.'
                : 'Trouvez le meilleur fournisseur d\'énergie adapté à vos besoins.'
              }
            </p>
          </div>

          {/* Search and Filter */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder={i18n.language === 'es' ? 'Buscar un proveedor...' : 'Rechercher un fournisseur...'}
                  className="pl-10 border-sage-200 focus:border-sage-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="provider-search-input"
                />
              </div>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="w-full sm:w-40 border-sage-200 focus:border-sage-500">
                  <MapPin className="w-4 h-4 mr-2 text-sage-400" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FR">
                    <div className="flex items-center gap-2">
                      <span>🇫🇷</span>
                      France
                    </div>
                  </SelectItem>
                  <SelectItem value="ES">
                    <div className="flex items-center gap-2">
                      <span>🇪🇸</span>
                      España
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Info Banner */}
      <section className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">
                  {i18n.language === 'es' ? 'Comparación transparente' : 'Comparaison transparente'}
                </h3>
                <p className="text-blue-700 text-sm">
                  {i18n.language === 'es' 
                    ? 'Comparamos las ofertas de electricidad con datos públicos. Los precios mostrados son estimaciones actualizadas mensualmente.'
                    : 'Nous comparons les offres d\'électricité avec des données publiques. Les prix affichés sont des estimations mises à jour mensuellement.'
                  }
                </p>
              </div>
              <div className="hidden sm:flex items-center gap-2 text-blue-600">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">
                  {i18n.language === 'es' ? 'Act. mensual' : 'Maj. mensuelle'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Grid */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-sage-900">
                {i18n.language === 'es' ? 'Proveedores de Electricidad' : 'Fournisseurs d\'Électricité'} {getCountryFlag(selectedCountry)}
              </h2>
              <p className="text-sage-600">
                {filteredProviders.length} {i18n.language === 'es' ? 'proveedor' : 'fournisseur'}{filteredProviders.length > 1 ? (i18n.language === 'es' ? 'es' : 's') : ''} {i18n.language === 'es' ? 'encontrado' : 'trouvé'}{filteredProviders.length > 1 ? 's' : ''}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-gray-200 rounded-lg h-80"></div>
                </div>
              ))}
            </div>
          ) : filteredProviders.length === 0 ? (
            <div className="text-center py-16">
              <Zap className="w-16 h-16 text-sage-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-sage-700 mb-2">
                {i18n.language === 'es' ? 'No se encontraron proveedores' : 'Aucun fournisseur trouvé'}
              </h3>
              <p className="text-sage-500">
                {i18n.language === 'es' ? 'Intenta modificar tus criterios de búsqueda' : 'Essayez de modifier vos critères de recherche'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProviders.map((provider) => {
                const features = provider.features || [];
                
                return (
                  <Card 
                    key={provider.id}
                    className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg overflow-hidden bg-white"
                    data-testid={`provider-card-${provider.id}`}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="w-16 h-16 rounded-xl overflow-hidden mb-3 bg-gray-100 flex items-center justify-center">
                            {provider.logo_url ? (
                              <img 
                                src={provider.logo_url} 
                                alt={`${provider.name} logo`}
                                className="max-w-full max-h-full object-contain"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div 
                              className="w-full h-full flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: provider.primary_color, display: provider.logo_url ? 'none' : 'flex' }}
                            >
                              {provider.name.charAt(0)}
                            </div>
                          </div>
                          <CardTitle className="text-xl font-bold text-sage-900 mb-1">
                            {provider.name}
                          </CardTitle>
                          <div className="flex items-center gap-2 text-sm text-sage-600">
                            <MapPin className="w-4 h-4" />
                            {provider.country === 'FR' ? (i18n.language === 'es' ? 'Francia' : 'France') : 'España'}
                          </div>
                        </div>
                        <Badge 
                          style={{ backgroundColor: provider.primary_color + '20', color: provider.primary_color }}
                          className="border-0"
                        >
                          {i18n.language === 'es' ? 'Energía' : 'Énergie'}
                        </Badge>
                      </div>

                      {/* Pricing Display */}
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 mb-4 border border-green-200">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-700 mb-1">
                            {provider.pricing.kwh.toFixed(4)}€
                          </div>
                          <div className="text-sm text-green-600 mb-2">/ kWh</div>
                          <div className="text-lg font-semibold text-sage-800">
                            ~{provider.monthlyEstimate}€{i18n.language === 'es' ? '/mes' : '/mois'}
                          </div>
                          <div className="text-xs text-sage-600">
                            {i18n.language === 'es' ? 'Est. 300 kWh/mes' : 'Est. 300 kWh/mois'}
                          </div>
                        </div>
                      </div>

                      {/* Features */}
                      <div className="flex flex-wrap gap-2 mb-4">
                        {features.map((feature, index) => {
                          const FeatureIcon = feature.icon;
                          return (
                            <div 
                              key={index}
                              className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-${feature.color}-100 text-${feature.color}-700`}
                            >
                              <FeatureIcon className="w-3 h-3" />
                              {feature.label}
                            </div>
                          );
                        })}
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <Separator className="mb-4" />
                      
                      <div className="space-y-3">
                        <div className="flex gap-2">
                          <a 
                            href={`tel:${saveaPhoneTel}`}
                            className="inline-flex items-center justify-center flex-1 text-sm text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-300 py-2 px-4 rounded-lg"
                            style={{ backgroundColor: provider.primary_color }}
                          >
                            <Phone className="w-4 h-4 mr-2" />
                            {isSpanish ? 'Hablar con Experto SAVEA' : 'Parler à un Expert SAVEA'}
                          </a>
                          
                          {provider.website_url && (
                            <Button 
                              variant="outline" 
                              onClick={() => visitWebsite(provider.website_url)}
                              className="border-sage-300 text-sage-700 hover:bg-sage-50 shadow-sm"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          )}

                          {/* Link to detailed provider page for major providers - Supprimé pour EDF et Engie */}
                          {['TotalEnergies', 'Octopus'].includes(provider.name) && (
                            <Button 
                              asChild
                              variant="ghost" 
                              size="sm"
                              className="border-sage-200 text-sage-600 hover:bg-sage-50"
                            >
                              <Link to={isSpanish ? `/es/proveedor/${provider.name.toLowerCase()}` : `/fr/fournisseur/${provider.name.toLowerCase()}`}>
                                <Info className="w-4 h-4" />
                              </Link>
                            </Button>
                          )}
                        </div>
                        
                        {/* Numéro fournisseur affiché mais PAS cliquable */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                            <Phone className="w-3 h-3" />
                            <span>
                              {isSpanish ? 'Número proveedor' : 'Numéro fournisseur'} : <span className="font-mono">{provider.phone}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600 max-w-4xl mx-auto">
              <strong>{i18n.language === 'es' ? 'Aviso:' : 'Avertissement :'}</strong> {i18n.language === 'es' 
                ? 'Este sitio ofrece comparación de ofertas energéticas. La información proporcionada se ofrece a título informativo. Los precios mostrados son estimaciones basadas en datos públicos, actualizados mensualmente. Podemos recibir comisiones sin afectar tu precio final. Para tarifas exactas, contacta directamente a los proveedores.'
                : 'Ce site propose une comparaison d\'offres énergétiques. Les informations fournies sont données à titre indicatif. Les prix affichés sont des estimations basées sur des données publiques, mises à jour mensuellement. Nous pouvons recevoir des commissions sans affecter votre prix final. Pour les tarifs exacts, contactez directement les fournisseurs.'
              }
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};